<template>
  <b-card
    no-body
    class="mb-5"
    :header-class="newBooking || isCheckoutPage ? 'text-white font-weight-bold' : 'font-weight-bold'"
    :header-bg-variant="newBooking || isCheckoutPage ? 'success' : 'light'"
  >
    <!-- Header with icon and booking counter badge -->
    <template #header>
      <div class="d-flex align-items-center">
        <b-icon icon="calendar-check" class="mr-2 calendar-icon" />
        <strong class="mb-0">{{ $t("pages.new_booking.your_bookings_header") }}</strong>
        <span :class="['booking-count-badge', headerBadgeClass, 'ml-2']">{{ bookingCount }}</span>
      </div>
    </template>

    <!-- Info when there are no bookings -->
    <div v-if="!newBooking && bookings.length === 0" class="text-center text-muted py-4">
      <b-icon icon="info-circle" font-scale="1.5" class="mb-2" />
      <p class="mb-0">{{ $t("pages.new_booking.no_selected_bookings") }}</p>
    </div>

    <!-- Active (new) booking -->
    <b-list-group v-if="newBooking" flush>
      <b-list-group-item class="d-flex justify-content-between align-items-center">
        <div>
          <b-icon icon="grid-3x3-gap" class="mr-2" />
          {{ $t("pages.new_booking.shelf_type_name", { shelfTypeName: newBooking.shelfTypeName }) }}
          <div v-if="newBooking.bookingRequest" class="text-muted small">#{{ newBooking.bookingRequest }}</div>
        </div>
        <b-button v-if="!isCheckoutPage" size="sm" variant="danger" @click="$emit('deleteNewBooking')">
          <b-icon icon="trash" />
        </b-button>
      </b-list-group-item>
      <b-list-group-item>
        <b-icon icon="clock" class="mr-2" />
        {{ $t("pages.new_booking.booking_period_name", { bookingPeriodName: newBooking.bookingPeriodLabel }) }}
      </b-list-group-item>
      <b-list-group-item>
        <strong>{{ formatMoney(newBooking.discountedPrice, market) }}</strong>
        <div v-if="newBooking.discountPercent && newBooking.isDiscountActive" class="text-success small">
          {{ $t("pages.new_booking.booking_info_discount_text", { discountPercent: newBooking.discountPercent }) }}
        </div>
      </b-list-group-item>
      <!-- Additional Services -->
      <b-list-group-item v-if="newBooking.bookingAddons?.length">
        <b-icon icon="tools" class="mr-2" /><strong>{{ $t('pages.new_booking.additional_services') }}:</strong>
        <ul class="list-unstyled mb-0 mt-2">
          <li
            v-for="svc in newBooking.bookingAddons"
            :key="svc.serviceId"
            class="d-flex justify-content-between"
          >
            <span>{{ svc.serviceName }} ×{{ svc.quantity }}</span>
            <strong>{{ formatMoney((svc.discountedPrice || svc.originalPrice) * svc.quantity, market) }}</strong>
          </li>
        </ul>
      </b-list-group-item>
      <b-list-group-item>
        <b-icon icon="calendar" class="mr-2" />
        {{ $t("pages.new_booking.start_date", { startDate: newBooking.dateStart || newBooking.dateStartCustomer }) }}
      </b-list-group-item>
      <b-list-group-item>
        <b-icon icon="calendar2-check" class="mr-2" />
        {{ $t("pages.new_booking.end_date", { endDate: newBooking.dateEnd || newBooking.dateEndCustomer }) }}
      </b-list-group-item>
      <b-list-group-item v-if="newBooking.eligibleClosingDays.length">
        <b-icon icon="clock" class="mr-2" />
        {{ $t("pages.new_booking.eligible_closing_days", { count: newBooking.eligibleClosingDays.length }) }}
      </b-list-group-item>
    </b-list-group>

    <!-- Divider between new and saved bookings -->
    <b-list-group-item
      v-if="bookings.length && newBooking"
      class="bg-light text-center text-muted"
    >
      <b-icon icon="bookmark" class="mr-2" />
      {{ $t("pages.new_booking.saved_bookings_header") }}
    </b-list-group-item>

    <!-- Saved bookings -->
    <b-list-group
      v-for="info in visibleBookings"
      :key="info.bookingRequest"
      flush
    >
      <b-list-group-item class="d-flex justify-content-between align-items-center">
        <div>
          <b-icon icon="grid-3x3-gap" class="mr-2" />
          {{ $t("pages.new_booking.shelf_type_name", { shelfTypeName: info.shelfTypeName }) }}
          <div v-if="info.bookingRequest" class="text-muted small">#{{ info.bookingRequest }}</div>
        </div>
        <b-button
          size="sm"
          variant="danger"
          @click="$emit('deleteBooking', info)"
          v-if="!isCheckoutPage || (isBookingExtension && isCustomerView)"
        >
          <b-icon icon="trash" />
        </b-button>
      </b-list-group-item>
      <b-list-group-item>
        <b-icon icon="clock" class="mr-2" />
        {{ $t("pages.new_booking.booking_period_name", { bookingPeriodName: info.bookingPeriodLabel }) }}
      </b-list-group-item>
      <b-list-group-item>
        <strong>{{ formatMoney(info.discountedPrice, market) }}</strong>
        <div v-if="info.discountPercent && info.isDiscountActive" class="text-success small">
          {{ $t("pages.new_booking.booking_info_discount_text", { discountPercent: info.discountPercent }) }}
        </div>
      </b-list-group-item>
      <!-- Additional Services -->
      <b-list-group-item v-if="info.bookingAddons?.length">
        <b-icon icon="tools" class="mr-2" /><strong>{{ $t('pages.new_booking.additional_services') }}:</strong>
        <ul class="list-unstyled mb-0 mt-2">
          <li
            v-for="svc in info.bookingAddons"
            :key="svc.serviceId"
            class="d-flex justify-content-between"
          >
            <span>{{ svc.serviceName }} ×{{ svc.quantity }}</span>
            <strong>{{ formatMoney((svc.discountedPrice || svc.originalPrice) * svc.quantity, market) }}</strong>
          </li>
        </ul>
      </b-list-group-item>
      <b-list-group-item>
        <b-icon icon="calendar" class="mr-2" />
        {{ $t("pages.new_booking.start_date", { startDate: info.dateStart || info.dateStartCustomer }) }}
      </b-list-group-item>
      <b-list-group-item>
        <b-icon icon="calendar2-check" class="mr-2" />
        {{ $t("pages.new_booking.end_date", { endDate: info.dateEnd || info.dateEndCustomer }) }}
      </b-list-group-item>
      <b-list-group-item v-if="info.eligibleClosingDays.length">
        <b-icon icon="clock" class="mr-2" />
        {{ $t("pages.new_booking.eligible_closing_days", { count: info.eligibleClosingDays.length }) }}
      </b-list-group-item>
    </b-list-group>

    <!-- Toggle for show more/less -->
    <div v-if="bookings.length >= 3" class="text-center my-2">
      <b-button
        size="sm"
        variant="outline-secondary"
        @click="toggleShowAll"
      >
        {{ showAll ? $t("pages.new_booking.button_show_less_bookings") : $t("pages.new_booking.button_show_more_bookings") }}
      </b-button>
    </div>

    <!-- Total summary -->
    <b-list-group-item
      v-if="newBooking || bookings.length"
      class="d-flex justify-content-between font-weight-bold bg-light mt-2 border-top"
    >
      <span>{{ $t('pages.new_booking.title_total') }}</span>
      <span>{{ formatMoney(getBookingWithAddonsTotal(bookings.concat(newBooking || [])), market) }}</span>
    </b-list-group-item>

    <!-- Fixed action buttons -->
    <div
      v-if="!isCheckoutPage && (newBooking || bookings.length)"
      class="fixed-footer-actions d-flex justify-content-center p-2"
    >
      <b-button
        size="sm"
        variant="success"
        class="mr-2"
        @click="$emit('confirm')"
      >
        <b-icon icon="check2-circle" class="mr-1" />
        {{ $t("pages.new_booking.button_proceed") }}
      </b-button>
      <b-button
        v-if="newBooking"
        size="sm"
        variant="primary"
        @click="$emit('addMore')"
      >
        <b-icon icon="plus-circle" class="mr-1" />
        {{ $t("pages.new_booking.button_add_more") }}
      </b-button>
    </div>
  </b-card>
</template>

<script>
import Formatting from '@base/mixins/Formatting.vue';
import TableUtils from "@base/mixins/TableUtils.vue";

export default {
  name: 'BookingSummaryCard',
  mixins: [ Formatting, TableUtils ],

  props: {
    bookings: {
      type: Array,
      required: true,
    },
    newBooking: {
      type: Object,
      default: null,
    },
    market: {
      type: Object,
      required: true
    },
    isCheckoutPage: {
      type: Boolean,
      default: false,
    },
    isBookingExtension: {
      type: Boolean,
      default: false,
    },
    isCustomerView: {
      type: Boolean,
      default: false,
    }
  },

  data() {
    return {
      showAll: false, // Controls whether all bookings are shown
    };
  },

  computed: {
    // Total number of bookings
    bookingCount() {
      return this.bookings.length + (this.newBooking ? 1 : 0);
    },
    // Controls how many bookings are displayed (2 existing + 1 pending)
    visibleBookings() {
      return this.showAll ? this.bookings : this.bookings.slice(0, 2);
    },
    headerBadgeClass() {
      // Return badge style based on current header background
      if (this.newBooking || this.isCheckoutPage) {
        return 'bg-white text-dark'; // for green header
      } else {
        return 'bg-dark text-white'; // for gray header
      }
    },
  },

  methods: {
    toggleShowAll() {
      this.showAll = !this.showAll;
    },

    getBookingWithAddonsTotal(bookings) {
      return bookings.reduce((total, booking) => {
        const basePrice = parseFloat(booking.discountedPrice || 0);

        const addonsTotal = (booking.bookingAddons || []).reduce((sum, svc) => {
          const unit = parseFloat(svc.discountedPrice ?? svc.originalPrice ?? 0);
          return sum + unit * svc.quantity;
        }, 0);

        return total + basePrice + addonsTotal;
      }, 0);
    }
  },
};
</script>

<style scoped>
.fixed-footer-actions {
  position: sticky;
  bottom: 0;
  background-color: #fff;
  border-top: 1px solid #dee2e6;
  z-index: 1000;
}

.booking-count-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.15rem 0.5rem;
  border-radius: 0.25rem;
  line-height: 1;
  align-self: center;
  display: inline-flex;
  justify-content: center;
  align-items: center;
}
.calendar-icon {
  font-size: 1.2rem;
}
</style>
